import * as THREE from 'three';
import OrbitControls from 'sunzi-three-orbitcontrols';
import FBXLoader from 'three-fbxloader-offical';

export interface Zen3dOptions {
  /** 更换的片段名称 */
  objectNames?: string[];
  /** 贴图更换的片段名称 */
  imageNames?: Zen3dImageName[];
  /** 颜色更换的片段名称 */
  colorNames?: Zen3dColorName[];
  /** 自动旋转 */
  autoRotate?: boolean;
  /** 需要灯光 */
  spotLight?: boolean;
  ambientLightColor?: number;
  /** 需要灯光透明度 */
  ambientLightOpacity?: number;
  /** 相机位置 */
  cameraPosition?: number[];
}

export interface Zen3dImageName {
  name: string;
  source: string;
}

export interface Zen3dColorName {
  name: string;
  color: number;
}

export interface Zen3dObject {
  [key: string]: any
}

class zen3d {
  public objectNames?: string[] = [];
  public imageNames: Zen3dImageName[] = [];
  public colorNames: Zen3dColorName[] = [];
  public autoRotate: boolean = true;
  public camera: THREE.PerspectiveCamera;
  public scene: THREE.Scene;
  public renderer: THREE.WebGLRenderer;
  public object?: THREE.Object3D;
  public objects: Zen3dObject = {};
  public spotLight?: THREE.SpotLight;
  public texture: THREE.Texture;
  public textureloader: THREE.TextureLoader;
  public rafs: number[] = [];
  public orbitControls: any;

  constructor(options: Zen3dOptions) {
    if (options.autoRotate) this.autoRotate = options.autoRotate;
    this.objectNames = options.objectNames?.map(item => item.toLowerCase());
    if (options.imageNames) this.imageNames = options.imageNames.map(({ name, source }) => ({ name: name.toLowerCase(), source }));
    if (options.colorNames) this.colorNames = options.colorNames.map(({ name, color }) => ({ name: name.toLowerCase(), color }));
    this.camera = new THREE.PerspectiveCamera(45, 1, 1, 2000);  //透视投影]
    //照相机位置在z轴正方向距离
    if (options.cameraPosition)
      this.camera.position.set(options.cameraPosition[0], options.cameraPosition[1], options.cameraPosition[2]);
    else
      this.camera.position.set(0, 50, 500);
    this.camera.lookAt(new THREE.Vector3(0, 50, 0));  //照相机直射原点
    this.scene = new THREE.Scene(); //设置场景背景, 0xa0a0a0
    // 环境光
    const light = new THREE.AmbientLight(options.ambientLightColor || 0xffffff, options.ambientLightOpacity || 0.7);
    this.scene.add(light);
    // 打光
    if (options.spotLight) {
      this.spotLight = new THREE.SpotLight(0xffffff);
      this.spotLight.position.set(-231, 1373, 203);
      this.spotLight.castShadow = false;
      this.spotLight.shadowCameraNear = 2;
      this.spotLight.shadowCameraFar = 200;
      this.spotLight.shadowCameraFov = 30;
      this.spotLight.distance = 0;
      this.spotLight.angle = -0.3;
      this.scene.add(this.spotLight);
    }
    this.texture = new THREE.Texture();
    this.textureloader = new THREE.TextureLoader();
    this.renderer = new THREE.WebGLRenderer({ alpha: true, antialias: true, precision: "highp" });
    this.renderer.sortObjects = false;
    this.renderer.setPixelRatio(window.devicePixelRatio);
    this.renderer.shadowMap.enabled = true;
    this.orbitControls = new OrbitControls(this.camera, this.renderer.domElement);
    this.orbitControls.enableZoom = false;  // 禁止缩放
    this.orbitControls.update();
  }

  /** 加载fbx文件 */
  public async loadFBX(pathFbx: string) {
    return new Promise(resolve => {
      if (this.object) resolve();
      new FBXLoader().load(pathFbx, (object: any) => {
        this.object = object;
        this.scene.add(object);
        object.traverse((child: any) => {
          this.objects[child.name.toLowerCase()] = child;
          this.animate();
          resolve();
        });
      });
    });
  }

  /** 渲染到dom节点 */
  public async render(container: HTMLElement, pathFbx: string) {
    if (this.object === undefined)
      await this.loadFBX(pathFbx);
    this.camera.aspect = container.clientWidth / container.clientHeight;
    this.camera.updateProjectionMatrix();
    this.renderer.setSize(container.clientWidth, container.clientHeight);
    container.appendChild(this.renderer.domElement);
  }

  /** 更新纹理 */
  public async updateTexture(source: string) {
    return new Promise(resolve => {
      const image = new Image();
      this.texture.image = image;
      image.onload = () => {
        this.objectNames?.map((key: string) => {
          const child = this.objects[key];
          child.material = new THREE.MeshBasicMaterial({ map: this.texture });
        });
        this.texture.needsUpdate = true;
        this.colorNames.forEach((item: Zen3dColorName) => {
          const child = this.objects[item.name];
          child.material.color.set(item.color);
        });
        Promise.all(this.imageNames.map((item: Zen3dImageName) => new Promise(_resolve => {
          const child = this.objects[item.name];
          this.textureloader.load(item.source, (texture: THREE.Texture) => {
            child.material = new THREE.MeshBasicMaterial({
              map: texture,
            });
            child.needsUpdate = true;
            _resolve()
          });
        }))).then(resolve);
      }
      image.crossOrigin = 'anonymous',
      image.src = source;
    });
  }

  public animate() {
    this.cancel();
    if(this.autoRotate && this.object)
      this.object.rotation.y += 0.010;
    this.rafs.push(requestAnimationFrame(this.animate.bind(this)));
    this.renderer.render(this.scene, this.camera);
  }

  public setAutoRotate(rotate: boolean) {
    this.autoRotate = rotate;
  }

  public cancel() {
    this.rafs.forEach(item => {
      cancelAnimationFrame(item);
    });
  }
}

export default zen3d;
